import { App } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import type { UnresolvedVendorErrorCodeFormValues } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/types';
import {
  buildUpdateUnresolvedPayload,
  isConflictError,
  isUnresolvedEditable,
} from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/utils';
import { UnresolvedVendorErrorCodeService } from '~/app/services/errorManagement/unresolved-vendor-error-code.service';

async function invalidateUnresolvedLists(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['unresolved-vendor-error-codes'],
    }),
    queryClient.invalidateQueries({
      queryKey: ['vendor-error-codes'],
    }),
  ]);
}

export function useUnresolvedEditDrawer() {
  const { t } = useTranslation('errorManagement');
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] =
    useState<UnresolvedVendorErrorCodeResponseDto | null>(null);
  const [hasConflict, setHasConflict] = useState(false);

  const closeEditDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerRecord(null);
    setHasConflict(false);
  };

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      values,
      expectedVersion,
    }: {
      id: string;
      values: UnresolvedVendorErrorCodeFormValues;
      expectedVersion: number;
    }) =>
      UnresolvedVendorErrorCodeService.update(
        id,
        buildUpdateUnresolvedPayload(values, expectedVersion),
      ),
    onSuccess: async () => {
      message.success(t('unresolved.messages.updateSuccess'));
      closeEditDrawer();
      await invalidateUnresolvedLists(queryClient);
    },
    onError: (error) => {
      if (isConflictError(error)) {
        setHasConflict(true);
        message.error(t('unresolved.messages.conflict'));
        return;
      }
      message.error(t('unresolved.messages.updateFailed'));
    },
  });

  const openEdit = (record: UnresolvedVendorErrorCodeResponseDto) => {
    if (!isUnresolvedEditable(record.status)) {
      return;
    }
    setDrawerRecord(record);
    setHasConflict(false);
    setIsDrawerOpen(true);
  };

  const reloadServerCopy = async () => {
    if (!drawerRecord) {
      return;
    }
    try {
      const fresh = await UnresolvedVendorErrorCodeService.getById(
        drawerRecord.id,
      );
      setDrawerRecord(fresh);
      setHasConflict(false);
      message.success(t('unresolved.messages.reloadSuccess'));
    } catch {
      message.error(t('unresolved.messages.reloadFailed'));
    }
  };

  const handleSave = async (values: UnresolvedVendorErrorCodeFormValues) => {
    if (!drawerRecord) {
      return;
    }
    await updateMutation.mutateAsync({
      id: drawerRecord.id,
      values,
      expectedVersion: drawerRecord.version,
    });
  };

  return {
    isDrawerOpen,
    drawerRecord,
    hasConflict,
    isSubmitting: updateMutation.isPending,
    openEdit,
    closeEditDrawer,
    handleSave,
    reloadServerCopy,
  };
}
